import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next"; // Assuming you're using i18n

interface ContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactDialog = ({ isOpen, onClose }: ContactDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [studioName, setStudioName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Make the API call to trigger a contact us
      const response = await fetch(`${import.meta.env.VITE_ADMIN_CONTACTUS_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, name, studioName }),
      });

      if (!response.ok) {
        throw new Error("Failed to send contact us");
      }

      toast({
        title: t("contactUs.requestSent"),
        description: t("contactUs.weWillGetBack"),
        className: "bg-green-500 text-white border-green-600",
        duration: 5000,
      });
      onClose();
    } catch (error) {
      console.error("Contact us failed:", error);
      toast({
        title: t("contactUs.toastErrorTitle"),
        description: t("contactUs.toastErrorDescription"),
        variant: "destructive",
        duration: 5000,
      });
    }
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("contactUs.title")}</DialogTitle>
          <DialogDescription>
            {t("contactUs.getInTouch")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("contactUs.name")}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("contactUs.enterName")}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("contactUs.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("contactUs.enterEmail")}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="studioName">{t("contactUs.studioName")}</Label>
            <Input
              id="studioName"
              value={studioName}
              onChange={(e) => setStudioName(e.target.value)}
              placeholder={t("contactUs.enterStudioName")}
              required
            />
          </div>
          <Alert variant="destructive" className="bg-orange-50">
            <AlertDescription>
              {t("contactUs.alertStudioProfessional")}
            </AlertDescription>
          </Alert>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t("contactUs.submitting") : t("contactUs.submit")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
